// Stage2.jsx
import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { useFormContext } from "../createworkflow/FormContext";
import CustomizedDialogs from "./CustomizedDialogs";
import { ErrorBoundary } from "../common/ErrorBoundary";
import Select from "react-select";
import { FcDatabase } from "react-icons/fc";
import { MdCategory } from "react-icons/md";

import {
  DataFrequency,
  ForecastPeriods,
  GranularityList
} from "../../Utils/dataTemporialSettings";
import styled from "styled-components";
import { getForecastPeriods, getProductHeirarchy } from "../../services/ApiWorkflow";
import { setValue, isEmpty, displayMessage, generateMonthYearLabels } from "../../Utils/helper";
import { getDateRanges } from '../../services/ApiWorkflow';
import Loader from '../common/Loader'
import { toggleErrorVisibility, stageLayoutStyles, stageFormStyles, StageNavButtons } from "./helper";
import { DataContext } from './DataContext';
import { months, weeks } from '../../Utils/dataSeasonality'

// ADD: import MenuContext to control tab navigation
import MenuContext from "./MenuContext";
import { getGeographyHeirarchy } from "../../Utils/dataGeography";

export default function Stage2() {

  const { formData, updateFormData } = useFormContext();
  const { activeTab, setActiveTab } = useContext(MenuContext);
  const [modalShow, setModalShow] = React.useState(false);

  console.log("Stage2 Rendered: ", formData);
  // State variables
  const [selDataFrequency, setSelDataFrequency] = useState(formData?.stage2?.data_frequency);
  const [selProductCategory, setSelProductCategory] = useState(formData?.stage2?.product_category);
  const [selForecastFrequency, setSelForecastFrequency] = useState(formData?.stage2?.forecast_frequency);
  const [disabledGranularity, setDisabledGranularity] = useState([]);
  const [defaultGranularity, setDefaultGranularity] = useState(formData?.stage2?.forecast_frequency);

  const [trainPercentage, setTrainPercentage] = useState(formData?.stage2?.train_percentage || 80);
  const [testPercentage, setTestPercentage] = useState(formData?.stage2?.test_percentage || 20);

  const [dateObject, setDateObject] = useState({});
  const [error, setError] = useState(null);
  const [isLoadingDateRange, setIsLoadingDateRange] = useState(false); // ✅ Specific loader for date range

  const trainPercentageRef = useRef();
  const testPercentageRef = useRef();

  const refDataFrequency = useRef(null);
  const refLblerrDataFrequency = useRef();
  const refForecastPeriod = useRef();
  const refLblerrForecastPeriod = useRef();
  const refLblerrProductCategory = useRef();

  const { products, isDataloading } = useContext(DataContext);
  const productCategories = useMemo(() => {
    return products?.data?.level1?.map((elem) => ({
      label: elem.name,
      value: elem.level1_id,
    })) || [];
  }, [products]);
  // ✅ FIXED: Only load date range data
  useEffect(() => {
    const init = async () => {
      try {
        if (!formData?.stage1?.data_upload_id) {
          displayMessage('danger', 'Insufficient Data', 'Please Upload file in Initialize workflow');
          return;
        }

        setIsLoadingDateRange(true);
        let _dateObject = await getDateRanges(formData?.stage1?.data_upload_id);

        if (_dateObject) {
          setDateObject(_dateObject);
          setDate(_dateObject);
        } else {
          displayMessage('danger', 'Data Error', 'Failed to fetch date ranges');
        }
      } catch (err) {
        console.error("Error initializing Stage2:", err);
        displayMessage('danger', 'Initialization Error', err.message || 'Failed to initialize data');
        setError(err);
      } finally {
        setIsLoadingDateRange(false);
      }
    };

    init();
  }, []); // eslint-disable-line

  useEffect(() => {
    try {
      if (!formData?.stage2?.isValid) {
        const { data_frequency, forecast_period, product_category } = formData?.stage2?.errors || {};

        toggleErrorVisibility(data_frequency, refDataFrequency.current?.controlRef, refLblerrDataFrequency.current);
        toggleErrorVisibility(forecast_period, refForecastPeriod.current?.controlRef, refLblerrForecastPeriod.current);
        toggleErrorVisibility(product_category, refLblerrProductCategory.current?.controlRef, refLblerrProductCategory.current);
      }
    } catch (error) {
      console.log(error);
    }
  }, []); // eslint-disable-line


  useEffect(() => {
    if (!formData?.stage2?.product_category && productCategories?.length > 0) {
      handleChangeProductCategory(productCategories[0]);
    }
  }, [productCategories, formData?.stage2?.product_category]);


  const setDate = (_dateObject) => {
    updateFormData("stage2", {
      ...formData.stage2,
      ['start_date']: _dateObject.avail_from,
      ['end_date']: _dateObject.avail_to,
    });
  };

  const changeDate = (e) => {
    if (isLoadingDateRange || !dateObject?.avail_to) {
      displayMessage('warning', 'Please Wait', 'Data is still loading');
      return;
    }

    updateFormData("stage2", {
      ...formData.stage2,
      ['start_date']: e.target.value,
      ['end_date']: dateObject.avail_to
    });
  };

  const RECOMMENDED_TRAIN_SPLIT = 80;

  const applyTrainingSplit = (value) => {
    const safe = Math.max(0, Math.min(100, parseInt(value, 10) || 0));
    setTrainPercentage(safe);
    setTestPercentage(100 - safe);

    updateFormData("stage2", {
      ...formData.stage2,
      ['train_percentage']: safe,
      ['test_percentage']: 100 - safe
    });
  };

  const handleTrainingPeriodChange = (e) => {
    applyTrainingSplit(e.target.value);
  };

  const handleModelItemClick = (e) => {
    setModalShow(true);
  };

  const handleChangeDataFrequency = (selected) => {
    setSelDataFrequency(selected.value);
    let result = DataFrequency.find(elem => elem.value === selected.value)?.disabledValues || [];

    setDisabledGranularity(result);
    let defaultGranValue = ['transaction', 'daily'].includes(selected.value.toLowerCase()) ? 'Weekly' : selected.value;
    setDefaultGranularity(defaultGranValue);

    setSelForecastFrequency(defaultGranValue);
    funcUpdateSeasonalityData(defaultGranValue);

    updateFormData("stage2", {
      ...formData.stage2,
      ['data_frequency']: selected.value,
      ['forecast_frequency']: defaultGranValue,
      ['defaultGranValue']: defaultGranValue
    });

    toggleErrorVisibility(selected.value === null, refDataFrequency.current?.controlRef, refLblerrDataFrequency.current);
  };

  function handleChangeProductCategory(selected) {
    setSelProductCategory(selected.value);
    updateFormData("stage2", {
      ...formData.stage2,
      ['product_category']: selected.value
    });
  };

  function funcUpdateSeasonalityData(frequency) {
    let seasonality_data = [];
    switch (frequency.toLowerCase()) {
      case 'weekly': seasonality_data = weeks; break;
      case 'monthly': seasonality_data = months; break;
      default: seasonality_data = []; break;
    }

    updateFormData("stage5", {
      ...formData.stage5,
      ['seasonality_data']: seasonality_data
    });
  }

  const rdGranularityOnChangeHandler = (e) => {
    setValue('session', 'form', "granularity", e.target.id);
    setSelForecastFrequency(e.target.id);

    updateFormData("stage2", {
      ...formData.stage2,
      ['forecast_frequency']: e.target.id
    });
    funcUpdateSeasonalityData(e.target.id);
  };

  const handleChangeForecastPeriod = (selected) => {
    console.log("hihello");

    console.log("selectedddddddd", selected);

    updateFormData("stage2", {
      ...formData.stage2,
      ['forecast_period']: selected.value
    });

    toggleErrorVisibility(selected.value === null, refForecastPeriod.current?.controlRef, refLblerrForecastPeriod.current);
  };

  const formatDate = (date) => {
    if (!date) return "";
    const [year, month, day] = date.split("-");
    return `${day}-${month}-${year}`;
  };

  const customSelectStyles = {
    control: (baseStyles, state) => ({
      ...baseStyles,
      borderColor: state.isFocused ? '#7aceb6' : '#85acc7',
      outline: 0,
      boxShadow: state.isFocused ? '0 0 0 2px rgba(122, 206, 182, 0.2)' : 'none',
      fontSize: '20px',
      padding: '4px'
    }),
  };

  // Navigation handlers
  const handleNext = () => {
    // Validate required fields
    if (!formData?.stage2?.data_frequency) {
      displayMessage('warning', 'Validation Error', 'Please select a data frequency');
      toggleErrorVisibility(true, refDataFrequency.current?.controlRef, refLblerrDataFrequency.current);
      return;
    }

    if (!formData?.stage2?.forecast_period) {
      displayMessage('warning', 'Validation Error', 'Please select a forecast horizon');
      toggleErrorVisibility(true, refForecastPeriod.current?.controlRef, refLblerrForecastPeriod.current);
      return;
    }

    if (!formData?.stage2?.product_category) {
      displayMessage('warning', 'Validation Error', 'Please select a product category');
      toggleErrorVisibility(true, refLblerrProductCategory.current?.controlRef, refLblerrProductCategory.current);
      return;
    }

    updateFormData("stage2", {
      ...formData.stage2
    });
    setActiveTab && setActiveTab("Stage3");
  };

  const handleBack = () => {
    updateFormData("stage2", {
      ...formData.stage2
    });
    setActiveTab && setActiveTab("Stage1");
  };

  const stage2CardStyle = {
    ...stageLayoutStyles.card,
    background: "#fafbfd",
  };

  const stage2SectionBox = {
    ...stageFormStyles.sectionBox,
    background: "#ffffff",
    border: "1px solid #c4e0f0",
    marginBottom: 0,
    height: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
  };

  const stage2Emphasis = {
    fontWeight: 600,
    color: "#0c3c54",
  };

  const stage2Metric = {
    fontWeight: 700,
    color: "#0c3c54",
    fontSize: 14,
  };


  return (
    <ErrorBoundary>
      <div style={stageLayoutStyles.wrapper}>
        <div style={stage2CardStyle}>


          {/* Content Grid — paired rows so left/right cards align */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
            <div style={stage2SectionBox}>
              <h3 style={stageFormStyles.sectionTitle}>Data Availability Range</h3>

              {isLoadingDateRange ? (
                <div className="flex items-center justify-center py-4">
                  <Loader size="small" />
                </div>
              ) : (
                <>
                  <p style={stageFormStyles.hint}>
                    Available: {dateObject?.avail_from ? formatDate(dateObject.avail_from) : "—"} to{" "}
                    {dateObject?.avail_to ? formatDate(dateObject.avail_to) : "—"}
                  </p>
                  <p style={{ ...stageFormStyles.hint, marginBottom: 8 }}>
                    <span style={stage2Emphasis}>Start Date</span>
                  </p>
                  <div className="de-stage2-date">
                    <input
                      type="date"
                      value={formData?.stage2?.start_date || ""}
                      onChange={(e) => changeDate(e)}
                      min={dateObject?.avail_from || ""}
                      max={dateObject?.avail_to || ""}
                      disabled={!dateObject?.avail_from}
                      style={{
                        ...stageFormStyles.input,
                        fontSize: 13,
                        width: "100%",
                        maxWidth: 200,
                        padding: "6px 10px",
                        opacity: !dateObject?.avail_from ? 0.5 : 1,
                      }}
                    />
                  </div>
                </>
              )}
            </div>

            <div
              style={{
                ...stage2SectionBox,
                justifyContent: "flex-start",
              }}
            >
              <h3 style={{ ...stageFormStyles.sectionTitle, marginBottom: 0 }}>
                Training / Testing Split
              </h3>
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  minHeight: 0,
                }}
              >
                <p style={{ ...stageFormStyles.hint, marginBottom: 12 }}>
                  <span style={stage2Metric}>{trainPercentage}%</span> training /{" "}
                  <span style={stage2Metric}>{testPercentage}%</span> testing
                </p>
                <div className="relative" style={{ paddingTop: 4, paddingBottom: 4 }}>
                  <div className="h-2 bg-gray-200 rounded-full relative">
                    <div
                      className="h-2 bg-[#E06639] rounded-full transition-all duration-300"
                      style={{ width: `${trainPercentage}%` }}
                    />
                    {/* Recommended split marker */}
                    <div
                      className="absolute top-1/2 -translate-y-1/2"
                      style={{
                        left: `${RECOMMENDED_TRAIN_SPLIT}%`,
                        width: 2,
                        height: 14,
                        background: "#0c3c54",
                        borderRadius: 1,
                        transform: "translate(-50%, -50%)",
                      }}
                      title={`Recommended: ${RECOMMENDED_TRAIN_SPLIT} / ${100 - RECOMMENDED_TRAIN_SPLIT}`}
                    />
                    <div
                      className="absolute top-1/2 w-4 h-4 bg-[#E06639] rounded-full transform -translate-y-1/2 shadow border-2 border-white"
                      style={{ left: `calc(${trainPercentage}% - 8px)`, zIndex: 1 }}
                    />
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={trainPercentage}
                    onChange={handleTrainingPeriodChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 12,
                    padding: "8px 10px",
                    background: "#eff7fc",
                    border: "1px solid #c4e0f0",
                    borderRadius: 8,
                  }}
                >
                  <iconify-icon
                    icon="mdi:lightbulb-on-outline"
                    width="16"
                    height="16"
                    style={{ color: "#0c3c54", flexShrink: 0 }}
                  />
                  <span
                    style={{
                      ...stageFormStyles.hint,
                      marginBottom: 0,
                      fontSize: 12,
                    }}
                  >
                    An{" "}
                    <strong style={{ color: "#0c3c54" }}>
                      {RECOMMENDED_TRAIN_SPLIT} / {100 - RECOMMENDED_TRAIN_SPLIT}
                    </strong>{" "}
                    split is recommended for most forecasts.
                  </span>
                  {trainPercentage !== RECOMMENDED_TRAIN_SPLIT && (
                    <button
                      type="button"
                      onClick={() => applyTrainingSplit(RECOMMENDED_TRAIN_SPLIT)}
                      style={{
                        marginLeft: "auto",
                        flexShrink: 0,
                        fontFamily: "Poppins, sans-serif",
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#fff",
                        background: "#E06639",
                        border: "none",
                        borderRadius: 6,
                        padding: "4px 10px",
                        cursor: "pointer",
                      }}
                    >
                      Use {RECOMMENDED_TRAIN_SPLIT} / {100 - RECOMMENDED_TRAIN_SPLIT}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div style={stage2SectionBox}>
              <h3 style={stageFormStyles.sectionTitle}>Data Frequency</h3>

              {isLoadingDateRange ? (
                <div className="flex items-center justify-center py-4">
                  <Loader size="small" />
                </div>
              ) : (
                <>
                  <p style={stageFormStyles.hint}>
                    Selected: <strong>{formData?.stage2?.data_frequency || "Monthly"}</strong>
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {DataFrequency.map((opt) => {
                      const isSelected = formData?.stage2?.data_frequency === opt.value;
                        // const isDisabled = opt.value === "Monthly";
                        const isDisabled = false;

                      return (
                        <button
                          key={opt.value}
                          type="button"
                          disabled={isDisabled}
                          onClick={() => !isDisabled && handleChangeDataFrequency(opt)}
                          style={{
                            ...stageFormStyles.chipBtn,
                            background: isSelected ? "#E06639" : "#fff",
                            color: isSelected ? "#fff" : "#0c3c54",
                            borderColor: isSelected ? "#E06639" : "#e6eef4",
                            opacity: isDisabled ? 0.5 : 1,
                            cursor: isDisabled ? "not-allowed" : "pointer",
                          }}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            <div style={stage2SectionBox}>
              <h3 style={stageFormStyles.sectionTitle}>Forecast Granularity</h3>
              <p style={stageFormStyles.hint}>
                Current: <strong>{formData?.stage2?.defaultGranValue || "Monthly"}</strong>
              </p>
              <div className="mt-2">
                <Stage2RadiobuttonList
                  DefaultValue={formData?.stage2?.defaultGranValue || "Monthly"}
                  DisabledValues={disabledGranularity}
                  OnSelectChange={rdGranularityOnChangeHandler}
                />
              </div>
            </div>

            <div style={stage2SectionBox}>
              <h3 style={stageFormStyles.sectionTitle}>Forecast Horizon</h3>
              <p style={stageFormStyles.hint}>
                Current: <strong>{formData?.stage2?.forecast_period || "Not Selected"}</strong>
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {selForecastFrequency && getForecastPeriods(selForecastFrequency)
                  .slice(0, 3)
                  .map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleChangeForecastPeriod(option)}
                      style={{
                        ...stageFormStyles.chipBtn,
                        background: formData?.stage2?.forecast_period === option.value ? "#E06639" : "#fff",
                        color: formData?.stage2?.forecast_period === option.value ? "#fff" : "#0c3c54",
                        borderColor: formData?.stage2?.forecast_period === option.value ? "#E06639" : "#e6eef4",
                      }}
                    >
                      {option.label}
                    </button>
                  ))}

                {selForecastFrequency && getForecastPeriods(selForecastFrequency).length > 3 && (
                  <select
                    value={formData?.stage2?.forecast_period || ""}
                    onChange={(e) => handleChangeForecastPeriod({ label: e.target.value, value: e.target.value })}
                    style={{
                      ...stageFormStyles.input,
                      width: "auto",
                      padding: "6px 10px",
                    }}
                  >
                    <option value="">--Select Period--</option>
                    {getForecastPeriods(selForecastFrequency)
                      .slice(3)
                      .map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                  </select>
                )}
              </div>
            </div>

            <div style={stage2SectionBox}>
              <h3 style={stageFormStyles.sectionTitle}>
                Product Category <span className="text-red-500">*</span>
              </h3>
              <p style={stageFormStyles.hint}>
                Selected:{" "}
                <strong>
                  {(() => {
                    const selected = productCategories?.find(
                      (opt) => opt.value === formData?.stage2?.product_category
                    );
                    return selected ? selected.label : "Not Selected";
                  })()}
                </strong>
              </p>
              {isDataloading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader size="small" />
                </div>
              ) : !productCategories?.length ? (
                <div className="text-[14px] text-red-500 mt-2">
                  No product categories available
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 mt-2">
                  {productCategories.map((opt) => {
                    const isSelected = formData?.stage2?.product_category === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleChangeProductCategory(opt)}
                        style={{
                          ...stageFormStyles.chipBtn,
                          background: isSelected ? "#E06639" : "#fff",
                          color: isSelected ? "#fff" : "#0c3c54",
                          borderColor: isSelected ? "#E06639" : "#e6eef4",
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}
              <label
                id="lblerr_product_category"
                ref={refLblerrProductCategory}
                className="invalid-feedback hidden"
              >
                * Required
              </label>
            </div>
          </div>

          {/* Hidden form elements */}
          <div className="hidden">
            <Select
              key="dataFrequency"
              ref={refDataFrequency}
              options={DataFrequency.map(opt => ({
                ...opt,
                isDisabled: opt.value !== 'Monthly'
              }))}
              onChange={handleChangeDataFrequency}
              defaultValue={{ label: formData?.stage2?.data_frequency, value: formData?.stage2?.data_frequency }}
              styles={customSelectStyles}
            />

            {!isDataloading && productCategories?.length && (
              <Select
                key="productCategory"
                onChange={handleChangeProductCategory}
                options={productCategories}
                defaultValue={productCategories?.find(elem => elem.value === formData?.stage2?.product_category) || null}
                styles={customSelectStyles}
              />
            )}

            <Select
              key="forecastPeriod"
              ref={refForecastPeriod}
              options={getForecastPeriods(selForecastFrequency)}
              onChange={handleChangeForecastPeriod}
              defaultValue={{ label: formData?.stage2?.forecast_period, value: formData?.stage2?.forecast_period }}
              styles={customSelectStyles}
            />

            <input ref={trainPercentageRef} type="hidden" value={trainPercentage} />
            <input ref={testPercentageRef} type="hidden" value={testPercentage} />

            <label ref={refLblerrDataFrequency} className="text-red-600 hidden" style={stageFormStyles.errorText}>* Required</label>
            <label ref={refLblerrProductCategory} className="text-red-600 hidden" style={stageFormStyles.errorText}>* Required</label>
            <label ref={refLblerrForecastPeriod} className="text-red-600 hidden" style={stageFormStyles.errorText}>* Required</label>
          </div>

          <StageNavButtons onBack={handleBack} onNext={handleNext} />

          <style>{`
            .de-stage2-date input[type="date"] {
              font-size: 13px !important;
              font-family: Poppins, sans-serif !important;
              color: #2c3e50 !important;
              line-height: 1.4 !important;
              min-height: 34px !important;
            }
          `}</style>
        </div>
      </div>
    </ErrorBoundary>
  );
}

function Stage2RadiobuttonList({
  DefaultValue,
  DisabledValues,
  OnSelectChange,
}) {
  const { formData, updateFormData } = useFormContext();
  const [checkedOption, setCheckedOption] = useState(DefaultValue || 'Monthly');

  useEffect(() => {
    setCheckedOption(DefaultValue || 'Monthly');
  }, [DefaultValue]);

  const handleRadioChange = (e) => {
    setCheckedOption(e.target.value);
    OnSelectChange(e);
  };

  const isItemDisabled = (item) => {
    return DisabledValues.includes(item);
  };

  // const granularityOptions = ['Weekly', 'Monthly', 'Quarterly'];
  const granularityOptions = ['Weekly', 'Monthly', 'Quarterly'];

  return (
    <div className="flex flex-wrap gap-2">
      {granularityOptions.map((option, index) => (
        <label key={index} className="flex items-center cursor-pointer">
          <button
            type="button"
            onClick={() => {
              if (!isItemDisabled(option)) {
                const event = { target: { value: option, id: option } };
                handleRadioChange(event);
              }
            }}
            disabled={isItemDisabled(option)}
            style={{
              ...stageFormStyles.chipBtn,
              background: checkedOption === option ? "#E06639" : "#fff",
              color: checkedOption === option ? "#fff" : "#0c3c54",
              borderColor: checkedOption === option ? "#E06639" : "#c4e0f0",
              opacity: isItemDisabled(option) ? 0.5 : 1,
              cursor: isItemDisabled(option) ? "not-allowed" : "pointer",
              padding: "6px 14px",
            }}
          >
            {option}
          </button>
          <input
            id={option}
            type="radio"
            name="granularity"
            value={option}
            checked={checkedOption === option}
            disabled={isItemDisabled(option)}
            onChange={handleRadioChange}
            className="hidden"
          />
        </label>
      ))}
    </div>
  );
}
